export interface JournalEntry {
  session: string;
  extension: "mp4" | "webm";
  bytes: number;
  updated: number;
}

interface Pending {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
}

/**
 * Client for the on-disk journal worker. Each append is durable before the
 * promise resolves, which is what makes recovery after an unclean exit real
 * rather than best-effort.
 */
export class Journal {
  private worker: Worker;
  private pending = new Map<number, Pending>();
  private nextId = 1;

  constructor() {
    this.worker = new Worker("/journal-worker.js", { type: "module" });
    this.worker.onmessage = (event: MessageEvent) => {
      const { id, ok, result, error } = event.data as {
        id: number;
        ok: boolean;
        result?: unknown;
        error?: string;
      };
      const slot = this.pending.get(id);
      if (!slot) return;
      this.pending.delete(id);
      if (ok) slot.resolve(result);
      else slot.reject(new Error(error ?? "journal failed"));
    };
    this.worker.onerror = (event) => {
      const error = new Error(event.message || "journal worker failed");
      for (const slot of this.pending.values()) slot.reject(error);
      this.pending.clear();
    };
  }

  private send<T>(op: string, payload: Record<string, unknown>, transfer: Transferable[] = []): Promise<T> {
    const id = this.nextId++;
    const message = { id, op, ...payload };
    return new Promise<T>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        if (this.pending.delete(id)) reject(new Error(`journal ${op} timed out`));
      }, 15_000);
      this.pending.set(id, {
        resolve: (value) => {
          window.clearTimeout(timer);
          resolve(value as T);
        },
        reject: (error) => {
          window.clearTimeout(timer);
          reject(error);
        },
      });
      this.worker.postMessage(message, transfer);
    });
  }

  async open(session: string, extension: "mp4" | "webm"): Promise<void> {
    await this.send<{ bytes: number }>("open", { session, extension });
  }

  async append(session: string, chunk: Blob): Promise<number> {
    const buffer = await chunk.arrayBuffer();
    const result = await this.send<{ bytes: number }>("append", { session, buffer }, [buffer]);
    return result.bytes;
  }

  async close(session: string): Promise<number> {
    const result = await this.send<{ bytes: number }>("close", { session });
    return result.bytes;
  }

  async read(session: string): Promise<{ blob: Blob; extension: "mp4" | "webm" }> {
    const result = await this.send<{ buffer: ArrayBuffer; extension: "mp4" | "webm" }>("read", { session });
    const type = result.extension === "webm" ? "video/webm" : "video/mp4";
    return { blob: new Blob([result.buffer], { type }), extension: result.extension };
  }

  async remove(session: string): Promise<void> {
    await this.send<{ bytes: number }>("delete", { session });
  }

  async list(): Promise<JournalEntry[]> {
    try {
      const result = await this.send<{ entries: JournalEntry[] }>("list", {});
      return result.entries;
    } catch {
      return [];
    }
  }

  terminate(): void {
    this.worker.terminate();
    for (const slot of this.pending.values()) slot.reject(new Error("journal closed"));
    this.pending.clear();
  }
}
